import QueryString from "query-string";
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert } from "../Alerts/actions";
import {
  appendOptimisticAiMessage,
  createAiConversationFailure,
  createAiConversationSuccess,
  fetchAiConversation as fetchAiConversationAction,
  fetchAiConversationFailure,
  fetchAiConversationSuccess,
  fetchAiConversations as fetchAiConversationsAction,
  fetchAiConversationsFailure,
  fetchAiConversationsSuccess,
  fetchAiLogsFailure,
  fetchAiLogsSuccess,
  sendAiMessageFailure,
  sendAiMessageSuccess,
} from "./actions";
import {
  CREATE_AI_CONVERSATION,
  FETCH_AI_CONVERSATION,
  FETCH_AI_CONVERSATIONS,
  FETCH_AI_LOGS,
  SEND_AI_MESSAGE,
} from "./constants";

function describeError(e) {
  if (!e) return "Unknown error";
  if (typeof e === "string") return e;
  if (e?.response?.data?.error) return e.response.data.error;
  if (e?.error) return e.error;
  if (e?.message) return e.message;
  return "Request failed";
}

function* fetchAiConversationsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/ai/conversations?${query}`,
    );
    yield put(fetchAiConversationsSuccess(data));
  } catch (e) {
    const msg = describeError(e);
    yield put(fetchAiConversationsFailure(msg));
    yield put(errorAlert(msg));
  }
}

function* fetchAiConversationSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/ai/conversations/${payload?.uuid}`,
    );
    yield put(fetchAiConversationSuccess(data));
  } catch (e) {
    const msg = describeError(e);
    yield put(fetchAiConversationFailure(msg));
    yield put(errorAlert(msg));
  }
}

function* createAiConversationSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(api(token).post, `/api/ai/conversations`, {
      resident_id: payload?.residentId,
      purpose: payload?.purpose || "chat",
    });
    yield put(createAiConversationSuccess(data));
    if (payload?.refetchList) {
      yield put(
        fetchAiConversationsAction({
          resident_id: payload.residentId,
          purpose: payload.purpose || "chat",
        }),
      );
    }
  } catch (e) {
    const msg = describeError(e);
    yield put(createAiConversationFailure(msg));
    yield put(errorAlert(msg));
  }
}

function* sendAiMessageSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  yield put(
    appendOptimisticAiMessage({
      role: "user",
      content: payload?.content,
    }),
  );
  try {
    const { data } = yield call(
      api(token).post,
      `/api/ai/conversations/${payload?.uuid}/messages`,
      {
        content: payload?.content,
        max_tokens: payload?.maxTokens,
      },
    );
    yield put(sendAiMessageSuccess(data));
  } catch (e) {
    const msg = describeError(e);
    yield put(sendAiMessageFailure(msg));
    yield put(errorAlert(msg));
  }
}

// AI Logs review: fetch the conversation list for a resident, then in parallel
// fetch each conversation's full message thread. Used by the read-only logs
// drawer to surface the agent's existing background governance + carepathway
// chatter without creating a new conversation.
function* fetchAiLogsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const residentId = payload?.residentId;
  if (!residentId) {
    yield put(fetchAiLogsFailure("Missing residentId"));
    return;
  }

  try {
    // List all conversations for this resident regardless of purpose.
    const listResp = yield call(
      api(token).get,
      `/api/ai/conversations?resident_id=${encodeURIComponent(residentId)}`,
    );
    const rows = Array.isArray(listResp) ? listResp : listResp?.data || [];

    if (!rows.length) {
      yield put(fetchAiLogsSuccess({ conversations: [] }));
      return;
    }

    // Fetch each conversation's full message thread in parallel.
    const details = yield all(
      rows.map((row) =>
        call(api(token).get, `/api/ai/conversations/${row.uuid}`),
      ),
    );

    // Merge templates_by_uuid maps from every conversation into one lookup so
    // the drawer can resolve any UUID the AI mentions — including templates
    // that aren't in the resident's care_pathway_queue.
    const templatesByUuid = {};
    const conversations = details.map((resp, idx) => {
      const c = resp?.data ?? resp;
      if (c?.templates_by_uuid && typeof c.templates_by_uuid === "object") {
        Object.assign(templatesByUuid, c.templates_by_uuid);
      }
      return {
        uuid: c?.uuid ?? rows[idx].uuid,
        purpose: c?.purpose ?? rows[idx].purpose,
        resident_id: c?.resident_id ?? rows[idx].resident_id,
        message_count: c?.message_count ?? rows[idx].message_count,
        created_at: c?.created_at ?? rows[idx].created_at,
        updated_at: c?.updated_at ?? rows[idx].updated_at,
        messages: Array.isArray(c?.messages) ? c.messages : [],
      };
    });

    yield put(fetchAiLogsSuccess({ conversations, templatesByUuid }));
  } catch (e) {
    const msg = describeError(e);
    yield put(fetchAiLogsFailure(msg));
    yield put(errorAlert(msg));
  }
}

export default function* AiConversationsSaga() {
  yield all([
    takeEvery(FETCH_AI_CONVERSATIONS, fetchAiConversationsSaga),
    takeLatest(FETCH_AI_CONVERSATION, fetchAiConversationSaga),
    takeEvery(CREATE_AI_CONVERSATION, createAiConversationSaga),
    takeEvery(SEND_AI_MESSAGE, sendAiMessageSaga),
    takeLatest(FETCH_AI_LOGS, fetchAiLogsSaga),
  ]);
}
